import { Platform } from '@angular/cdk/platform';
import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, AfterViewInit  } from '@angular/core';
import { MatDialogRef, MatExpansionPanel, MatTabGroup, MAT_DIALOG_DATA } from '@angular/material';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import dayjs from 'dayjs';
import { DateFormat } from 'src/app/core/class/date-format';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DocumentMohaService } from 'src/app/core/services/document-moha.service';
import { EnumCriteriaCode } from 'src/app/core/constants/criteria-code';
import { Subscription } from 'rxjs';

interface IReportCriterionRow {
  code: string;
  name: string;
  inputType: string;
  order?: number;
  parentCode?: string;
  controlField?: any;
  displayOrder?: string;
  level?: number;
  rowType: 'header' | 'subheader' | 'criterion';
  expanded?: boolean;
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MM YYYY',
  },
};

@Component({
  selector: 'app-view-properties-original-dialog',
  styleUrls: ['./view-properties-original-dialog.component.scss'],
  templateUrl: './view-properties-original-dialog.component.html',
  providers: [
    // DateFormat,
    { provide: MAT_DATE_LOCALE, useValue: 'vi' },
    {
      provide: DateAdapter,
      useClass: DateFormat,
      deps: [MAT_DATE_LOCALE, Platform],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class ViewPropertiesOriginalDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  canEdit = false;
  isEditMode = false;
  isSaving = false;
  isPermissionProperties = false;
  isVneidVerified = false;
  listDataSource: any[] = [];
  listDataTable: IReportCriterionRow[] = [];
  listDataUpdateSource: any[] = [];
  customerMohaId: any = null
  customerMohaName: any = null
  listCustomer: any[] = [];
  listColumnVersionHistory: string[] = [];
  listDataVersionHistory: any[] = [];
  optionsMap = new Map<string, any[]>();
  displayedColumns = ["stt", "nameCriteria", "infoTemplate"];
  headerColumns = ["groupHeader"];
  displayedHistoryColumns: string[] = []
  historyGroupColumns: { name: string; span: number }[] = [];
  historyGroupRowColumns: string[] = [];
  readonly historyColumnWidth = 200; // px, phải khớp width .col-cell trong scss
  isOriginalContract: boolean = false;
  titleChange: string = '';
  hideCriteriaUpdateHistory: boolean = false;
  @ViewChild('myTabGroup', { static: false }) myTabGroup: MatTabGroup;
  @ViewChild('inputName', { static: false }) inputName: ElementRef;
  @ViewChild('mainCriteriaPanel', { static: false }) mainCriteriaPanel: MatExpansionPanel;
  @ViewChild('criteriaContainer', { static: false }) criteriaContainer: ElementRef;
  isInforTab = 0
  fieldMap = new Map<string, any>()
  private readonly expandedGroups = new Map<string, boolean>();
  private readonly parentCodeMap = new Map<string, string>();

  private readonly representativeRequiredCodes = [
    EnumCriteriaCode.ndd_so_dinh_danh,
    EnumCriteriaCode.ndd_ho_ten,
    EnumCriteriaCode.ndd_quoc_tich,
    EnumCriteriaCode.ndd_ngay_sinh,
    EnumCriteriaCode.ndd_gioi_tinh,
    EnumCriteriaCode.ndd_dia_chi,
    EnumCriteriaCode.ndd_so_dien_thoai,
    EnumCriteriaCode.ndd_quan_he_nld
  ];
  private readonly vneidProtectedCodes = [
    EnumCriteriaCode.nld_ho_ten,
    EnumCriteriaCode.nld_ngay_sinh,
    EnumCriteriaCode.nld_so_dinh_danh,
    EnumCriteriaCode.nld_quoc_tich
  ];

  private readonly subscriptions = new Subscription();
  private readonly minDate = new Date();
  disabledFutureDate = (current: Date): boolean =>
    !!current && current.getTime() > this.minDate.getTime();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ViewPropertiesOriginalDialogComponent>,
    private documentMohaService: DocumentMohaService,
    private toastr: ToastrService,
    private translateService: TranslateService,
  ) {
    // them chuc nang close message băng Esc vaf click chuot
    dialogRef.disableClose = false;
  }

  ngOnInit() {
    this.listDataUpdateSource = this.data.listDataUpdateSource || [];
    this.canEdit = this.data.canEdit === true;
    this.isPermissionProperties = this.data.isPermissionProperties === true;
    this.isVneidVerified = this.data.isVneidVerified === true;
    this.listDataSource = this.canEdit
      ? (this.data.listDataSource || []).map(field => this.createEditableField(field))
      : (this.data.listDataSource || []);
    this.rebuildTableRows()
    this.customerMohaId = this.data.customerMohaId
    this.customerMohaName = this.data.customerMohaName
    this.listCustomer = this.data.listCustomer || [];
    this.listDataSource.forEach(field => {
      if (field.inputType === 'DROPDOWN') this.setDropdownOptions(field);
    });
    this.isOriginalContract = this.data.isOriginalContract;
    this.titleChange = this.data.titleChange;
    this.hideCriteriaUpdateHistory = !!this.data.hideCriteriaUpdateHistory;
    this.listDataVersionHistory = this.data.listDataVersionHistory || [];
    this.buildColumns(this.data.listColumnVersionHistory || [])
    this.initFieldMap(this.data.listColumnVersionHistory || [])
  }

  private createEditableField(field: any): any {
    if (field.inputType === 'HEADER') return { ...field };
    const isRequired = !this.isConditionallyRequiredField(field.code) && field.isRequired;
    const validators: ValidatorFn[] = isRequired ? [Validators.required] : [];
    if (field.inputType === 'TEXT') {
      validators.push(Validators.maxLength(field.maxLength || 255));
      if (field.regex) {
        try {
          validators.push(Validators.pattern(new RegExp(field.regex)));
        } catch (error) {
          console.warn('Invalid criterion regex:', field.code);
        }
      }
    }
    if (field.inputType === 'DATETIME') {
      validators.push(control => control.value && isNaN(new Date(control.value).getTime())
        ? { invalidDateFormat: true } : null);
    }
    const disabled = this.isVneidVerified && this.vneidProtectedCodes.indexOf(field.code) >= 0;
    let value = field.controlField ? field.controlField.value : null;
    // Select cần null khi chưa chọn; chuỗi rỗng vẫn bị coi là một giá trị đã chọn.
    if (!disabled && field.inputType === 'DROPDOWN' && (value === undefined || value === null || value === '')) {
      value = null;
    }
    if (field.inputType === 'DATETIME' && value) value = new Date(value);
    return {
      ...field,
      isRequired,
      baseValidators: validators,
      controlField: new FormControl({ value, disabled: true }, validators)
    };
  }

  startEditing(): void {
    if (!this.canEdit || this.isEditMode || this.isSaving) return;
    this.isEditMode = true;
    this.listDataSource.forEach(field => {
      if (field.inputType === 'HEADER' || !field.controlField) return;
      const isProtected = this.isVneidVerified && this.vneidProtectedCodes.indexOf(field.code) >= 0;
      if (!isProtected) field.controlField.enable({ emitEvent: false });
    });
    this.configureDynamicValidation(this.listDataSource);
  }

  trimText(element: any) {
    if (element.controlField.disabled) return;
    const value = element.controlField.value;
    if (typeof value === 'string') element.controlField.setValue(value.trim());
  }

  clearText(element: any) {
    if (this.isSaving || element.controlField.disabled) return;
    element.controlField.setValue('');
  }

  private focusFirstInvalidControl(): void {
    const invalidRow = this.listDataTable.find(row => row.controlField && row.controlField.invalid);
    if (!invalidRow) return;
    if (this.mainCriteriaPanel) this.mainCriteriaPanel.expanded = true;
    setTimeout(() => {
      if (!this.criteriaContainer) return;
      const host: HTMLElement = this.criteriaContainer.nativeElement;
      const container = Array.from(host.querySelectorAll<HTMLElement>('[data-validation-code]'))
        .find(element => element.getAttribute('data-validation-code') === invalidRow.code);
      if (!container) return;
      const target = container.querySelector<HTMLElement>('input:not([disabled]), .ant-select-selection') || container;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    });
  }

  save() {
    if (!this.isEditMode || this.isSaving) return;
    const fields = this.listDataSource.filter(field => field.inputType !== 'HEADER' && field.controlField);
    fields.forEach(field => {
      if (field.inputType === 'TEXT' && !field.controlField.disabled) this.trimText(field);
      field.controlField.markAsTouched();
      field.controlField.updateValueAndValidity();
    });
    this.validateDateRelations(this.listDataSource);
    if (fields.some(field => field.controlField.invalid || field.controlField.pending)) {
      this.expandedGroups.clear();
      this.rebuildTableRows();
      this.focusFirstInvalidControl();
      return;
    }
    this.isSaving = true;
    this.dialogRef.disableClose = true;
    const originalValues = new Map<string, any>();
    (this.data.listDataSource || []).forEach(field => {
      if (field.controlField) originalValues.set(field.code, field.controlField.value);
    });
    this.documentMohaService.updateMohaMetadata({
      contractId: this.data.contractId,
      contractMohaFields: fields.map(field => ({
        key: field.code,
        value: field.controlField.disabled && originalValues.has(field.code)
          ? originalValues.get(field.code)
          : field.controlField.value
      })),
      customerMohaId: this.customerMohaId,
      editProperty: null,
      contractMohaUpdateFields: null,
      originalContractId: null
    }).pipe(finalize(() => {
      this.isSaving = false;
      this.dialogRef.disableClose = false;
    })).subscribe((response: any) => {
      if (response && response.success) {
        this.toastr.success(this.translateService.instant('messages.update_success'));
        this.dialogRef.close({ updated: true });
      } else {
        this.toastr.error(response && response.message || 'Không thể cập nhật tiêu chí');
      }
    }, error => this.toastr.error(error && error.error && error.error.message || 'Không thể cập nhật tiêu chí'));
  }

  private setDropdownOptions(item: any): void {
    try {
      const options = typeof item.metadata === 'string'
        ? JSON.parse(item.metadata)
        : item.metadata;
      this.optionsMap.set(item.code, Array.isArray(options) ? options : []);
    } catch (error) {
      this.optionsMap.set(item.code, []);
      console.error(`Metadata dropdown không hợp lệ tại field ${item.code}:`, item.metadata, error);
    }
  }

  isBirthDateField(code: string): boolean {
    return code === EnumCriteriaCode.nld_ngay_sinh
      || code === EnumCriteriaCode.ndd_ngay_sinh;
  }

  private isConditionallyRequiredField(code: any): boolean {
    return this.representativeRequiredCodes.indexOf(code) >= 0
      || code === EnumCriteriaCode.nndh_bat_dau
      || code === EnumCriteriaCode.nndh_ket_thuc
      || code === EnumCriteriaCode.so_thang_thuc_hien
      || code === EnumCriteriaCode.ngay_ket_thuc_hdld;
  }

  private configureDynamicValidation(rows: any[]): void {
    if (!rows || !rows.length || !this.isEditMode) return;

    const watch = (code: string, update: () => void) => {
      const row = this.getRow(rows, code);
      if (row && row.controlField) {
        this.subscriptions.add(row.controlField.valueChanges.subscribe(update));
      }
    };

    const updateRepresentative = () => {
      const birthDate = this.getRowValue(rows, EnumCriteriaCode.nld_ngay_sinh);
      const isUnder15 = this.isUnder15YearsOld(birthDate);
      this.representativeRequiredCodes.forEach(code =>
        this.setConditionalRequired(this.getRow(rows, code), isUnder15)
      );
    };

    const updateHazardousJob = () => {
      const hazardousValue = this.getRowValue(rows, EnumCriteriaCode.nndh_is_doc_hai);
      const isHazardous = hazardousValue === true
        || String(hazardousValue) === '1'
        || String(hazardousValue).toLowerCase() === 'true';
      this.setConditionalRequired(this.getRow(rows, EnumCriteriaCode.nndh_bat_dau), isHazardous);
      this.setConditionalRequired(this.getRow(rows, EnumCriteriaCode.nndh_ket_thuc), isHazardous);
      this.validateDateRelations(rows);
    };

    const updateContractType = () => {
      const isLimitedTerm = String(this.getRowValue(rows, EnumCriteriaCode.loai_hop_dong)) === '1';
      this.setConditionalRequired(this.getRow(rows, EnumCriteriaCode.so_thang_thuc_hien), isLimitedTerm);
      this.setConditionalRequired(this.getRow(rows, EnumCriteriaCode.ngay_ket_thuc_hdld), isLimitedTerm);
      this.validateDateRelations(rows);
    };

    watch(EnumCriteriaCode.nld_ngay_sinh, () => {
      updateRepresentative();
      this.validateDateRelations(rows);
    });
    watch(EnumCriteriaCode.nndh_is_doc_hai, updateHazardousJob);
    watch(EnumCriteriaCode.nndh_bat_dau, () => this.validateDateRelations(rows));
    watch(EnumCriteriaCode.nndh_ket_thuc, () => this.validateDateRelations(rows));
    watch(EnumCriteriaCode.loai_hop_dong, updateContractType);
    watch(EnumCriteriaCode.ngay_bat_dau_hdld, () => this.validateDateRelations(rows));
    watch(EnumCriteriaCode.ngay_ket_thuc_hdld, () => this.validateDateRelations(rows));

    updateRepresentative();
    updateHazardousJob();
    updateContractType();
    this.validateDateRelations(rows);
  }

  private getRow(rows: any[], code: string): any {
    return rows.find((row: any) => row.code === code);
  }

  private getRowValue(rows: any[], code: string): any {
    const row = this.getRow(rows, code);
    return row && row.controlField ? row.controlField.value : null;
  }

  private setConditionalRequired(row: any, required: boolean): void {
    if (!row || !row.controlField) return;
    row.isRequired = required;
    const tableRow = this.listDataTable.find((item: IReportCriterionRow) => item.code === row.code);
    if (tableRow) {
      (tableRow as any).isRequired = required;
    }
    const validators = [...(row.baseValidators || [])];
    if (required) validators.push(Validators.required);
    row.controlField.setValidators(validators);
    row.controlField.updateValueAndValidity({ emitEvent: false });
  }

  private isUnder15YearsOld(value: any): boolean {
    const birthDate = this.toValidDate(value);
    if (!birthDate) return false;
    const fifteenthBirthday = new Date(
      birthDate.getFullYear() + 15,
      birthDate.getMonth(),
      birthDate.getDate()
    );
    return fifteenthBirthday.getTime() > new Date().getTime();
  }

  private toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private getDateOnlyTime(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }

  private setRelationError(row: any, errorKey: string, invalid: boolean): void {
    if (!row || !row.controlField) return;
    const errors = { ...(row.controlField.errors || {}) };
    if (invalid) {
      errors[errorKey] = true;
    } else {
      delete errors[errorKey];
    }
    row.controlField.setErrors(Object.keys(errors).length ? errors : null);
  }

  isDateRangeTouched(endCode: string): boolean {
    const startCode = endCode === EnumCriteriaCode.nndh_ket_thuc
      ? EnumCriteriaCode.nndh_bat_dau
      : EnumCriteriaCode.ngay_bat_dau_hdld;
    const start = this.getRow(this.listDataSource, startCode);
    const end = this.getRow(this.listDataSource, endCode);
    return !!((start && start.controlField.touched) || (end && end.controlField.touched));
  }

  private validateDateRelations(rows: any[]): void {
    const hazardousStart = this.getRow(rows, EnumCriteriaCode.nndh_bat_dau);
    const hazardousEnd = this.getRow(rows, EnumCriteriaCode.nndh_ket_thuc);
    const hazardousStartDate = this.toValidDate(hazardousStart && hazardousStart.controlField.value);
    const hazardousEndDate = this.toValidDate(hazardousEnd && hazardousEnd.controlField.value);
    this.setRelationError(
      hazardousEnd,
      'hazardousDateOrder',
      !!hazardousStartDate && !!hazardousEndDate
        && this.getDateOnlyTime(hazardousStartDate) > this.getDateOnlyTime(hazardousEndDate)
    );

    const contractStart = this.getRow(rows, EnumCriteriaCode.ngay_bat_dau_hdld);
    const contractEnd = this.getRow(rows, EnumCriteriaCode.ngay_ket_thuc_hdld);
    const employeeBirthDate = this.toValidDate(this.getRowValue(rows, EnumCriteriaCode.nld_ngay_sinh));
    const contractStartDate = this.toValidDate(contractStart && contractStart.controlField.value);
    const contractEndDate = this.toValidDate(contractEnd && contractEnd.controlField.value);

    this.setRelationError(
      contractStart,
      'contractStartNotAfterBirthDate',
      !!contractStartDate && !!employeeBirthDate
        && this.getDateOnlyTime(contractStartDate) <= this.getDateOnlyTime(employeeBirthDate)
    );
    this.setRelationError(
      contractEnd,
      'contractDateOrder',
      !!contractStartDate && !!contractEndDate
        && this.getDateOnlyTime(contractEndDate) < this.getDateOnlyTime(contractStartDate)
    );
  }

  getDisabledDateForField(row: any): ((current: Date) => boolean) | null {
    if (this.isBirthDateField(row.code)) return this.disabledFutureDate;

    if (row.code === EnumCriteriaCode.nndh_ket_thuc) {
      const startDate = this.toValidDate(this.getRowValue(this.listDataSource, EnumCriteriaCode.nndh_bat_dau));
      return (current: Date) => !!startDate
        && this.getDateOnlyTime(current) < this.getDateOnlyTime(startDate);
    }

    if (row.code === EnumCriteriaCode.ngay_bat_dau_hdld) {
      const birthDate = this.toValidDate(this.getRowValue(this.listDataSource, EnumCriteriaCode.nld_ngay_sinh));
      const endDate = this.toValidDate(this.getRowValue(this.listDataSource, EnumCriteriaCode.ngay_ket_thuc_hdld));
      return (current: Date) =>
        (!!birthDate && this.getDateOnlyTime(current) <= this.getDateOnlyTime(birthDate))
        || (!!endDate && this.getDateOnlyTime(current) > this.getDateOnlyTime(endDate));
    }

    if (row.code === EnumCriteriaCode.ngay_ket_thuc_hdld) {
      const startDate = this.toValidDate(this.getRowValue(this.listDataSource, EnumCriteriaCode.ngay_bat_dau_hdld));
      return (current: Date) => !!startDate
        && this.getDateOnlyTime(current) < this.getDateOnlyTime(startDate);
    }

    return null;
  }

  onDatePickerOpenChange(isOpen: boolean, row: any): void {
    if (!isOpen && row && row.controlField) {
      row.controlField.markAsTouched();
      this.validateDateRelations(this.listDataSource);
    }
  }


  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewInit() {
    this.dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => {
        const activeEl = document.activeElement as HTMLElement;
        activeEl.blur();
      });
    });
  }


  initFieldMap(dataStandard: any[]) {
    dataStandard.forEach(f => {
      if (f.code && !this.fieldMap.has(f.code)) {
        this.fieldMap.set(f.code, f)
      }
    })
  }

  getField(code: string) {
    return this.fieldMap.get(code)
  }


  buildColumns(dataStandard: any[]) {
    // API trả tiêu chí trùng code giữa các nguồn (get-all + các nhóm properties-for-update)
    // → chỉ giữ lần xuất hiện đầu tiên để tránh lặp cột
    const seen = new Set<string>();
    const uniqueFields = (dataStandard || []).filter(f => {
      if (!f.code || seen.has(f.code)) return false;
      seen.add(f.code);
      return true;
    });

    const fieldByCode = new Map<string, any>();
    uniqueFields.forEach(f => fieldByCode.set(f.code, f));

    const fields = uniqueFields.filter(x => x.inputType !== 'HEADER');

    // Tên nhóm = tên cha gần nhất (trim để so khớp chắc chắn)
    const groupNameOf = (f: any): string => {
      const parent = f.parentCode ? fieldByCode.get(f.parentCode) : null;
      return parent ? String(parent.name || parent.code || '').trim() : '';
    };

    // Các section trùng tên nhóm (VD "Dữ liệu chủ yếu HĐLĐ") nằm rải rác nên
    // phải xếp lại thứ tự cột cho các cột cùng tên nhóm đứng cạnh nhau,
    // khi đó mới gộp được thành 1 ô colspan duy nhất
    const groupOrder: string[] = [];
    const buckets = new Map<string, any[]>();
    fields.forEach(f => {
      const g = groupNameOf(f);
      let bucket = buckets.get(g);
      if (!bucket) {
        bucket = [];
        buckets.set(g, bucket);
        groupOrder.push(g);
      }
      bucket.push(f);
    });
    const orderedFields: any[] = [];
    groupOrder.forEach(g => orderedFields.push(...(buckets.get(g) || [])));
    this.displayedHistoryColumns = orderedFields.map(x => x.code);

    // Header nhóm: gom liên tiếp cùng tên → tăng colspan;
    // cột không có cha thì tên nhóm rỗng, mỗi cột một ô riêng
    this.historyGroupColumns = [];
    this.historyGroupRowColumns = [];
    orderedFields.forEach(f => {
      const g = groupNameOf(f);
      const last = this.historyGroupColumns[this.historyGroupColumns.length - 1];
      if (g && last && last.name === g) {
        last.span++;
      } else {
        this.historyGroupColumns.push({ name: g, span: 1 });
        this.historyGroupRowColumns.push('group_' + (this.historyGroupColumns.length - 1));
      }
    });
  }

  onNoClick() {
    if (!this.isSaving) this.dialogRef.close();
  }

  getFormatDate(date: string) {
    return dayjs(date).format('DD/MM/YYYY')
  }

  getDataDropdown(data: any, value: string) {
    if (!data || !data.metadata || value === null || value === undefined || value === '') return '';
    const listMetadata = JSON.parse(data.metadata);
    let valueView = listMetadata.find(e => e.value == value || e.value == value.toString());
    return valueView ? valueView.name : '';
  }

  private rebuildTableRows() {
    const nodes: IReportCriterionRow[] = (this.listDataSource || [])
      .filter((item: IReportCriterionRow) => !!item && !!item.code)
      .map((item: IReportCriterionRow) => ({
        ...item,
        expanded: item.inputType === 'HEADER'
          ? (this.expandedGroups.has(item.code) ? this.expandedGroups.get(item.code) : true)
          : undefined,
        rowType: item.inputType === 'HEADER' ? 'header' : 'criterion'
      }));

    const childrenMap = new Map<string, IReportCriterionRow[]>();
    this.parentCodeMap.clear();
    nodes.forEach(node => {
      const parentCode = node.parentCode || '';
      this.parentCodeMap.set(node.code, parentCode);
      const children = childrenMap.get(parentCode) || [];
      children.push(node);
      childrenMap.set(parentCode, children);
    });

    const effectiveOrder = (node: IReportCriterionRow): number => {
      const children = childrenMap.get(node.code) || [];
      return node.inputType === 'HEADER' && children.length
        ? Math.min(...children.map(effectiveOrder))
        : Number(node.order || 0);
    };
    childrenMap.forEach(children =>
      children.sort((first, second) => effectiveOrder(first) - effectiveOrder(second))
    );

    const rows: IReportCriterionRow[] = [];
    let runningOrder = 0;
    const appendNode = (node: IReportCriterionRow, displayOrder: string, level: number) => {
      const isHeader = node.inputType === 'HEADER';
      rows.push({
        ...node,
        displayOrder,
        level,
        rowType: isHeader ? 'subheader' : 'criterion'
      });
      if (isHeader) {
        (childrenMap.get(node.code) || []).forEach((child, index) =>
          appendNode(child, `${displayOrder}.${index + 1}`, level + 1)
        );
      }
    };

    const rootNodes = childrenMap.get('') || [];
    const rootHeaders = rootNodes.filter(node => node.inputType === 'HEADER');
    if (!rootHeaders.length) {
      rootNodes.forEach(node => {
        runningOrder++;
        appendNode(node, `${runningOrder}`, 1);
      });
    } else {
      rootHeaders.forEach(root => {
        rows.push({ ...root, displayOrder: null, level: 0, rowType: 'header' });
        (childrenMap.get(root.code) || []).forEach(child => {
          runningOrder++;
          appendNode(child, `${runningOrder}`, 1);
        });
      });
    }
    this.listDataTable = rows;
  }

  toggleGroup(row: IReportCriterionRow) {
    row.expanded = !row.expanded;
    this.expandedGroups.set(row.code, row.expanded);
  }

  isRowVisible(row: IReportCriterionRow): boolean {
    let parentCode = row.parentCode || '';
    while (parentCode) {
      if (this.expandedGroups.get(parentCode) === false) return false;
      parentCode = this.parentCodeMap.get(parentCode) || '';
    }
    return true;
  }

  trackByCode(_: number, row: IReportCriterionRow): string {
    return row.code;
  }

  isHeaderRow = (_: number, row: IReportCriterionRow) => row.rowType === 'header';
  isCriterionRow = (_: number, row: IReportCriterionRow) => row.rowType !== 'header';

  getDisplayValue(element: any): string {
    const value = element && element.controlField ? element.controlField.value : null;
    if (value === null || value === undefined || value === '') return '';
    if (element.inputType === 'DATETIME') return this.getFormatDate(value);
    if (element.inputType === 'DROPDOWN') {
      const options = this.optionsMap.get(element.code) || [];
      const selected = options.find(option => option.value == value);
      return selected ? selected.name : value;
    }
    return value;
  }
}
